import lodash from "lodash";
import { TreeNode } from "@/types/tree";
import { documentStore } from "@/store/store-accessor";
import {
  shouldApplySectionSchemeToHeader,
  applySectionSchemeToHeader
} from "@/futurelayer/headers-footers/overlay-headers";
import { ColorScheme } from "./types";
import chroma from "chroma-js";

/**
 * Convert a color scheme string to a CSS class name
 *
 * Examples:
 * - "primary[0]" → "primary-scheme-1"
 * - "neutral[5]" → "neutral-scheme-6"
 * - "secondary[0][2]" → "secondary-1-scheme-3"
 * - "secondary[1][0]" → "secondary-2-scheme-1"
 *
 * @param schemeString The scheme string (e.g., "primary[0]", "secondary[0][2]")
 * @returns CSS class name
 */
export function schemeStringToClassName(schemeString: string): string {
  if (!schemeString) return "";

  // Match primary or neutral: "primary[0]" or "neutral[5]"
  const primaryNeutralMatch = schemeString.match(/^(primary|neutral)\[(\d+)\]$/);
  if (primaryNeutralMatch) {
    const [, palette, index] = primaryNeutralMatch;
    return `${palette}-scheme-${parseInt(index) + 1}`;
  }

  // Match secondary: "secondary[0][2]" or "secondary[1][0]"
  const secondaryMatch = schemeString.match(/^secondary\[(\d+)\]\[(\d+)\]$/);
  if (secondaryMatch) {
    const [, paletteIndex, schemeIndex] = secondaryMatch;
    return `secondary-${parseInt(paletteIndex) + 1}-scheme-${parseInt(schemeIndex) + 1}`;
  }

  return "";
}

/**
 * Convert a CSS class name back to a scheme string
 *
 * Examples:
 * - "primary-scheme-1" → "primary[0]"
 * - "neutral-scheme-6" → "neutral[5]"
 * - "secondary-1-scheme-3" → "secondary[0][2]"
 * - "secondary-2-scheme-1" → "secondary[1][0]"
 *
 * @param className The CSS class name
 * @returns Scheme string or empty string if invalid
 */
export function classNameToSchemeString(className: string): string {
  if (!className) return "";

  // Match primary or neutral: "primary-scheme-1" or "neutral-scheme-6"
  const primaryNeutralMatch = className.match(/^(primary|neutral)-scheme-(\d+)$/);
  if (primaryNeutralMatch) {
    const [, palette, schemeNum] = primaryNeutralMatch;
    const index = parseInt(schemeNum) - 1;
    return `${palette}[${index}]`;
  }

  // Match secondary: "secondary-1-scheme-3" or "secondary-2-scheme-1"
  const secondaryMatch = className.match(/^secondary-(\d+)-scheme-(\d+)$/);
  if (secondaryMatch) {
    const [, paletteNum, schemeNum] = secondaryMatch;
    const paletteIndex = parseInt(paletteNum) - 1;
    const schemeIndex = parseInt(schemeNum) - 1;
    return `secondary[${paletteIndex}][${schemeIndex}]`;
  }

  return "";
}

/**
 * Check if a class name is a color scheme class
 *
 * @param className The class name to check
 * @returns True if it's a color scheme class
 */
export function isColorSchemeClass(className: string): boolean {
  if (!className) return false;

  // Match patterns like:
  // - primary-scheme-1
  // - neutral-scheme-2
  // - secondary-1-scheme-3
  return /^(primary|neutral|secondary-\d+)-scheme-\d+$/.test(className);
}

/**
 * Check if a class name (with or without leading dot) is a FutureLayer-managed class
 * This includes both color scheme classes and brand palette classes
 *
 * @param className The class name to check (e.g., "primary-scheme-1" or ".primary-scheme-1")
 * @returns True if it's a FutureLayer color scheme or brand palette class
 */
export function isFutureLayerColorClass(className: string): boolean {
  if (!className) return false;

  // Remove leading dot if present
  const classNameWithoutDot = className.startsWith('.') ? className.substring(1) : className;

  // Check if it's a scheme class (primary-scheme-1, neutral-scheme-2, secondary-1-scheme-3)
  const isSchemeClass = /^(primary|neutral|secondary-\d+)-scheme-\d+$/.test(classNameWithoutDot);

  // Check if it's a brand palette class (futurelayer-brand-primary, futurelayer-brand-neutral, futurelayer-brand-secondary)
  const isBrandClass = /^futurelayer-brand-(primary|neutral|secondary)$/.test(classNameWithoutDot);

  // Check if it's the overlay header class
  const isOverlayHeaderClass = classNameWithoutDot === 'futurelayer-header-is-overlay';

  return isSchemeClass || isBrandClass || isOverlayHeaderClass;
}

/**
 * Filter out FutureLayer-managed color classes from a list of class names
 * Used to exclude programmatically-generated classes from "missing selectors" warnings
 *
 * @param classNames Array of class names (with or without leading dots)
 * @returns Array with FutureLayer color classes removed
 */
export function removeFutureLayerColorClasses(classNames: string[]): string[] {
  return classNames.filter(className => !isFutureLayerColorClass(className));
}

/**
 * Get the color scheme CSS class name from an element's settings.advanced.classes
 *
 * @param classes The classes array from settings.advanced.classes
 * @returns The CSS class name or empty string if not found
 */
export function getColorSchemeClassNameFromClasses(classes: string[]): string {
  if (!Array.isArray(classes)) return "";

  const schemeClass = classes.find(cls => isColorSchemeClass(cls));
  return schemeClass || "";
}

/**
 * Get the color scheme string from an element's classes
 * Extracts the scheme string (e.g., "primary[0]", "secondary[0][2]") from the element's classes
 *
 * @param classes The classes array from settings.advanced.classes
 * @param defaultScheme The default scheme string to return if no scheme is found (default: "neutral[0]")
 * @returns The scheme string
 */
export function getColorSchemeStringFromClasses(classes: any, defaultScheme: string = "neutral[0]"): string {
  // Find the color scheme class
  const className = getColorSchemeClassNameFromClasses(classes);

  // Convert class name to scheme string
  const schemeString = classNameToSchemeString(className);

  // Return scheme string or default
  return schemeString || defaultScheme;
}

/**
 * Parse a scheme string into a ChosenColorScheme object
 *
 * @param schemeString The scheme string (e.g., "primary[0]", "secondary[0][2]")
 * @returns ChosenColorScheme object with parsed information
 */
export function parseSchemeString(schemeString: string): ChosenColorScheme {
  const category = getCategoryFromSchemeString(schemeString);
  const index = getSchemeIndexFromSchemeString(schemeString);

  return {
    schemeString,
    category: category || 'neutral',
    index: index ?? 0
  };
}

/**
 * Get the chosen color scheme from an element's classes
 *
 * @param classes The classes array from settings.advanced.classes
 * @param defaultScheme The default scheme string to use if none found (default: "neutral[0]")
 * @returns ChosenColorScheme object
 */
export function getChosenColorSchemeFromClasses(classes: any, defaultScheme: string = "neutral[0]"): ChosenColorScheme {
  const schemeString = getColorSchemeStringFromClasses(classes, defaultScheme);
  return parseSchemeString(schemeString);
}

/**
 * Color scheme category type
 */
export type ColorSchemeCategory = 'primary' | 'secondary1' | 'secondary2' | 'neutral' | null;

/**
 * Chosen color scheme data structure
 * Contains all derived information about the selected color scheme
 */
export interface ChosenColorScheme {
  schemeString: string;
  category: ColorSchemeCategory;
  index: number;
}

/**
 * Default chosen color scheme
 */
export const DEFAULT_CHOSEN_COLOR_SCHEME: ChosenColorScheme = {
  schemeString: "neutral[0]",
  category: 'neutral',
  index: 0
};

/**
 * All available color scheme categories
 */
export const ALL_CATEGORIES: ColorSchemeCategory[] = ['primary', 'secondary1', 'secondary2', 'neutral'];

/**
 * Get the category from a scheme string
 *
 * @param schemeString The scheme string (e.g., "primary[0]", "secondary[0][2]")
 * @returns The category ('primary', 'secondary1', 'secondary2', 'neutral', or null)
 */
export function getCategoryFromSchemeString(schemeString: string): ColorSchemeCategory {
  if (!schemeString) return null;

  if (schemeString.startsWith('primary[')) {
    return 'primary';
  } else if (schemeString.startsWith('secondary[0]')) {
    return 'secondary1';
  } else if (schemeString.startsWith('secondary[1]')) {
    return 'secondary2';
  } else if (schemeString.startsWith('neutral[')) {
    return 'neutral';
  }

  return null;
}

/**
 * Get the scheme index from a scheme string
 *
 * @param schemeString The scheme string (e.g., "primary[0]", "secondary[0][2]")
 * @returns The scheme index or null if invalid
 */
export function getSchemeIndexFromSchemeString(schemeString: string): number | null {
  if (!schemeString) return null;

  // Match primary or neutral: "primary[5]" or "neutral[3]"
  const primaryNeutralMatch = schemeString.match(/^(?:primary|neutral)\[(\d+)\]$/);
  if (primaryNeutralMatch) {
    return parseInt(primaryNeutralMatch[1]);
  }

  // Match secondary: "secondary[0][2]" or "secondary[1][5]"
  const secondaryMatch = schemeString.match(/^secondary\[\d+\]\[(\d+)\]$/);
  if (secondaryMatch) {
    return parseInt(secondaryMatch[1]);
  }

  return null;
}

/**
 * Build a scheme string from category and index
 *
 * @param category The category (e.g., "primary", "secondary1", "neutral")
 * @param index The index of the scheme
 * @returns The scheme string (e.g., "primary[0]", "secondary[0][2]")
 */
export function buildSchemeString(category: ColorSchemeCategory, index: number): string {
  if (!category) return "";

  if (category === "primary") {
    return `primary[${index}]`;
  } else if (category === "secondary1") {
    return `secondary[0][${index}]`;
  } else if (category === "secondary2") {
    return `secondary[1][${index}]`;
  } else if (category === "neutral") {
    return `neutral[${index}]`;
  }

  return "";
}

/**
 * Get a human-readable label for a color scheme
 * Extracts shade numbers from labels like "primary 100", "primary 900"
 *
 * @param scheme The color scheme
 * @param fallback Fallback string to return if shade numbers can't be extracted
 * @returns Label like "BG: 100 • Text: 900" or the fallback
 */
export function getColorSchemeLabel(scheme: any, fallback: string = ""): string {
  const bgLabel = scheme.background?.label || "";
  const textLabel = scheme.contrastedFromBackgroundForText?.label || "";

  const bgMatch = bgLabel.match(/(\d+)/);
  const textMatch = textLabel.match(/(\d+)/);

  if (bgMatch && textMatch) {
    return `BG: ${bgMatch[1]} • Text: ${textMatch[1]}`;
  }

  return fallback;
}

/**
 * Get a random scheme string and scheme from a category
 *
 * @param category The category to get a random scheme from
 * @param allSchemes All possible color schemes
 * @returns Object with schemeString and scheme, or null if category has no schemes
 */
export function getRandomSchemeForCategory(
  category: ColorSchemeCategory,
  allSchemes: any
): { schemeString: string; scheme: any } | null {
  if (!category) return null;

  let schemes: any[] = [];
  let schemeStringPrefix = '';

  if (category === 'primary') {
    schemes = allSchemes.primary || [];
    schemeStringPrefix = 'primary';
  } else if (category === 'secondary1') {
    schemes = allSchemes.secondary?.[0] || [];
    schemeStringPrefix = 'secondary[0]';
  } else if (category === 'secondary2') {
    schemes = allSchemes.secondary?.[1] || [];
    schemeStringPrefix = 'secondary[1]';
  } else if (category === 'neutral') {
    schemes = allSchemes.neutral || [];
    schemeStringPrefix = 'neutral';
  }

  if (schemes.length === 0) return null;

  const randomIndex = Math.floor(Math.random() * schemes.length);
  const scheme = schemes[randomIndex];
  const schemeString = `${schemeStringPrefix}[${randomIndex}]`;

  return { schemeString, scheme };
}

/**
 * Get a random scheme from any available category
 *
 * @param allSchemes All possible color schemes
 * @returns Object with schemeString and scheme, or null if no schemes available
 */
export function getRandomScheme(allSchemes: any): { schemeString: string; scheme: any } | null {
  const randomCategory = ALL_CATEGORIES[Math.floor(Math.random() * ALL_CATEGORIES.length)];
  return getRandomSchemeForCategory(randomCategory, allSchemes);
}

/**
 * Get a specific scheme from a category by index
 *
 * @param category The category to get the scheme from
 * @param index The index of the scheme
 * @param allSchemes All possible color schemes
 * @returns The color scheme or null if not found
 */
export function getSchemeForCategory(
  category: ColorSchemeCategory,
  index: number,
  allSchemes: any
): any | null {
  if (!category) return null;

  let schemes: any[] = [];

  if (category === 'primary') {
    schemes = allSchemes.primary || [];
  } else if (category === 'secondary1') {
    schemes = allSchemes.secondary?.[0] || [];
  } else if (category === 'secondary2') {
    schemes = allSchemes.secondary?.[1] || [];
  } else if (category === 'neutral') {
    schemes = allSchemes.neutral || [];
  }

  return schemes[index] || schemes[0] || null;
}

/**
 * Get available categories with their representative schemes
 * Returns categories that have at least one scheme available
 *
 * @param allSchemes All possible color schemes
 * @param selectedCategory The currently selected category
 * @param selectedIndex The index of the selected scheme
 * @returns Array of objects with category name and representative scheme
 */
export function getAvailableCategories(
  allSchemes: any,
  selectedCategory: ColorSchemeCategory,
  selectedIndex: number
): Array<{ name: ColorSchemeCategory; scheme: any }> {
  return ALL_CATEGORIES.map(categoryName => {
    const index = selectedCategory === categoryName ? selectedIndex : 0;
    const scheme = getSchemeForCategory(categoryName, index, allSchemes);

    return {
      name: categoryName,
      scheme
    };
  }).filter(cat => cat.scheme !== null);
}


/**
 * Get the brand palette class name from a scheme string
 * Examples:
 * - "primary[0]" → "futurelayer-brand-primary"
 * - "neutral[5]" → "futurelayer-brand-neutral"
 * - "secondary[0][2]" → "futurelayer-brand-secondary"
 * - "secondary[1][0]" → "futurelayer-brand-secondary"
 */
function getBrandPaletteClassName(schemeString: string): string {
  if (!schemeString) return "";

  if (schemeString.startsWith('primary[')) {
    return 'futurelayer-brand-primary';
  } else if (schemeString.startsWith('neutral[')) {
    return 'futurelayer-brand-neutral';
  } else if (schemeString.startsWith('secondary[')) {
    return 'futurelayer-brand-secondary';
  }

  return "";
}

/**
 * Check if a class name is a brand palette class
 */
function isBrandPaletteClass(className: string): boolean {
  return /^futurelayer-brand-(primary|neutral|secondary)$/.test(className);
}

/**
 * Copy color scheme classes from source element to target element
 * Removes any existing scheme/brand classes from target and adds the source's classes
 *
 * @param sourceElement The element to copy classes from
 * @param targetElementId The target element ID
 * @param targetElement The target element
 */
export function copyColorSchemeClassesToElement(
  sourceElement: TreeNode,
  targetElementId: TreeNode["id"],
  targetElement: TreeNode
): void {
  // Get the scheme class from the source
  const sourceClasses = lodash.get(
    sourceElement,
    "data.properties.settings.advanced.classes",
    []
  );

  const schemeClassName = getColorSchemeClassNameFromClasses(sourceClasses);
  if (!schemeClassName) return;

  // Get existing target classes
  const existingTargetClasses = lodash.get(
    targetElement,
    "data.properties.settings.advanced.classes",
    []
  );

  const targetClassesArray = Array.isArray(existingTargetClasses) ? existingTargetClasses : [];

  // Remove any existing scheme/brand classes from target
  const filteredTargetClasses = targetClassesArray.filter(
    cls => !isColorSchemeClass(cls) && !isBrandPaletteClass(cls)
  );

  // Add the source's scheme class to the target
  const updatedTargetClasses = [...filteredTargetClasses, schemeClassName];

  // Also add brand palette class if source has one
  const brandPaletteClass = sourceClasses.find((cls: string) => isBrandPaletteClass(cls));
  if (brandPaletteClass) {
    updatedTargetClasses.push(brandPaletteClass);
  }

  documentStore.unthrottledPropertyChanged({
    elementId: targetElementId,
    path: "settings.advanced.classes",
    value: updatedTargetClasses,
    meta: { snapshotLabel: "Copied color scheme classes" }
  });
}


/**
 * Check if a section element has a background image
 *
 * @param element The section element to check
 * @returns True if the element has a background image
 */
export function sectionHasBackgroundImage(element: TreeNode): boolean {
  return lodash.get(element, "data.properties.design.background.type") === "image";
}

/**
 * Get section design properties for sections with background images
 * Returns properties for overlay color and opacity based on the color scheme
 *
 * @param element The section element
 * @param scheme The selected color scheme
 * @returns Section design properties object
 */
export function getSectionDesignPropertiesForSectionWithBackgroundImage(
  element: TreeNode,
  scheme: ColorScheme
): Record<string, any> {
  if (!sectionHasBackgroundImage(element)) {
    return {};
  }

  const backgroundColor = scheme.background.value;

  if (!backgroundColor) {
    return {};
  }

  // Convert background color to CSS variable reference
  const bgVar = `var(--${scheme.background.cssVariableName})`;

  return {
    background: {
      overlay: {
        color: bgVar,
        opacity: 0.7
      }
    }
  };
}

/**
 * Set section design properties for sections with background images
 * Applies overlay color and opacity to sections with background images
 *
 * @param elementId The element ID
 * @param element The section element
 * @param scheme The selected color scheme
 */
export function setSectionDesignPropertiesForSectionWithBackgroundImage(
  elementId: TreeNode["id"],
  element: TreeNode,
  scheme: ColorScheme
): void {
  const overlayDesignProperties = getSectionDesignPropertiesForSectionWithBackgroundImage(element, scheme);
  const existingDesignProperties = lodash.get(element, "data.properties.design", {});
  const mergedDesign = lodash.merge({}, existingDesignProperties, overlayDesignProperties);

  documentStore.unthrottledPropertyChanged({
    elementId,
    path: "design",
    value: mergedDesign,
    meta: { snapshotLabel: "Applied color scheme overlay" }
  });
}

/**
 * Apply a selected color scheme to a section element
 * Adds the CSS class name to settings.advanced.classes array
 * The class will contain all color scheme styles from the global CSS
 * The class name is the single source of truth for the selected scheme
 */
export function applySelectedSchemeToElement(
  elementId: TreeNode["id"],
  element: TreeNode,
  schemeString: string,
  scheme: ColorScheme,
): void {

  // Add the CSS class name to settings.advanced.classes
  if (scheme) {
    const className = schemeStringToClassName(schemeString);
    const brandPaletteClass = getBrandPaletteClassName(schemeString);

    // Get existing classes - might be null, undefined, or an array
    const existingClasses = lodash.get(
      element,
      "data.properties.settings.advanced.classes"
    );

    // Ensure we have an array to work with
    const classesArray = Array.isArray(existingClasses) ? existingClasses : [];

    // Remove any existing color scheme classes and brand palette classes (keep all other classes)
    const filteredClasses = classesArray.filter(
      cls => !isColorSchemeClass(cls) && !isBrandPaletteClass(cls)
    );

    // Add the new color scheme class and brand palette class
    const updatedClasses = [...filteredClasses, className];
    if (brandPaletteClass) {
      updatedClasses.push(brandPaletteClass);
    }

    documentStore.unthrottledPropertyChanged({
      elementId,
      path: "settings.advanced.classes",
      value: updatedClasses,
      meta: { snapshotLabel: "Applied color scheme" }
    });

    if (sectionHasBackgroundImage(element)) {
      setSectionDesignPropertiesForSectionWithBackgroundImage(elementId, element, scheme);
    }

    // Apply scheme to header if this section wants overlay header behavior
    if (shouldApplySectionSchemeToHeader(elementId, element)) {
      applySectionSchemeToHeader(element);
    }
  }
}


/**
 * Sort color schemes by:
 * 1. Background luminance (lightest to darkest)
 * 2. For same background, text luminance (darkest to lightest)
 */
export function sortSchemes(schemes: ColorScheme[]): ColorScheme[] {
  return schemes.sort((a, b) => {
    const aBgLum = chroma(a.background.value?.toString() || '#000').luminance();
    const bBgLum = chroma(b.background.value?.toString() || '#000').luminance();

    // Sort by background luminance (lightest first = higher luminance)
    if (Math.abs(aBgLum - bBgLum) > 0.01) {
      return bBgLum - aBgLum; // Descending (lightest first)
    }

    // Same background, sort by text luminance (darkest first = lower luminance)
    const aTextLum = chroma(a.contrastedFromBackgroundForText.value?.toString() || '#fff').luminance();
    const bTextLum = chroma(b.contrastedFromBackgroundForText.value?.toString() || '#fff').luminance();
    return aTextLum - bTextLum; // Ascending (darkest first)
  });
}
